import IconThreeDots from "assets/img/icon-three-dots.svg?react"

import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { Guid } from "@/types"

import Routes from "@/routes"
import ShippingOrder, { ShippingOrderStatus, ShippingOrderType } from "models/ShippingOrder"
import { Flex } from "components/Flex"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton, InfoDropdownListItemLink } from "components/InfoDropdown"
import Text from "components/Text/Text"
import Status, { StatusType } from "components/Status/Status"
import Button from "components/Button/Button"
import ShippingOrderTransport from "./ShippingOrderTransport"
import ShippingOrderModal from "components/ShippingOrderModal/ShippingOrderModal"
import Reservation from "models/Reservation"

interface IShippingOrderSentToStockCoordinatorProps {
    shippingOrder: ShippingOrder
    reservation: Reservation
}

const ShippingOrderSentToStockCoordinator: FC<IShippingOrderSentToStockCoordinatorProps> = ({
    shippingOrder,
    reservation,
}) => {
    const { t } = useTranslation()
    const location = useLocation()
    const isCompleted = shippingOrder.status === ShippingOrderStatus.completed
    const isReturn = shippingOrder.type === ShippingOrderType.return
    const eventLocationAddress = shippingOrder.eventLocation?.address.printZipCityCountry() || ""
    const [showShippingOrderModal, setShowShippingOrderModal] = useState(false)

    function getLoadingListUrl(shippingOrderId: Guid) {
        return Routes.prepareShipping.getPath({ shippingOrderId }, { backUrl: location.pathname + location.search });
    }

    return (
        <>
            <div className="round-sm border-grey-200 p-3">
                <Flex alignItems="baseline"
                    justifyContent="space-between">
                    <Text type="h5" className="mb-1">{ShippingOrder.getTypeLabel(shippingOrder.type)} ({shippingOrder.number})</Text>
                    <InfoDropdown
                        label={() => (
                            <Button
                                className="ml-2"
                                theme="grey"
                                round
                                icon={<IconThreeDots />}
                            />
                        )}
                        maxWidth="280px"
                        placement="bottom-end"
                        content={
                            <InfoDropdownList>
                                <InfoDropdownListItemButton
                                    onClick={() => setShowShippingOrderModal(true)}>
                                    {t('shippingOrders.showShippingOrder')}
                                </InfoDropdownListItemButton>
                                <InfoDropdownListItemLink
                                    href={getLoadingListUrl(shippingOrder.id)}
                                    target="_blank">
                                    {t('shippingOrders.showLoadingList')}
                                </InfoDropdownListItemLink>
                                {!!shippingOrder.completionReportUri &&
                                    <InfoDropdownListItemLink
                                        href={shippingOrder.completionReportUri}
                                        target="_blank"
                                        download>
                                        {isReturn
                                            ? t('shippingOrders.downloadConfirmationOfReceipt')
                                            : t('shippingOrders.downloadOverallDeliveryNote')
                                        }
                                    </InfoDropdownListItemLink>
                                }
                            </InfoDropdownList>
                        } />
                </Flex>
                <Text>
                    {eventLocationAddress ? `${t(isReturn ? 'common.from' : 'common.to')} ${eventLocationAddress}` : ''}
                    {!!eventLocationAddress && shippingOrder.arrivalDate ? ' | ' : ''}
                    {(shippingOrder.arrivalDateMax || shippingOrder.arrivalDate) ? (shippingOrder.arrivalDateMax || shippingOrder.arrivalDate)?.format(t('config.dateLongTimeFormat')) : ''}
                </Text>
                <div className="mt-2">
                    {shippingOrder.transports.map((transport, index) => (
                        <div key={transport.id} className="border-grey-100-between">
                            <ShippingOrderTransport
                                sequence={index + 1}
                                transport={transport}
                                shippingOrderType={shippingOrder.type}
                                hasTransportSegmentLoadedAssets={shippingOrder.hasTransportSegmentLoadedAssets.bind(shippingOrder)}
                            />
                        </div>
                    ))}
                </div>
                <div className="mt-2">
                    <Status
                        type={isCompleted ? StatusType.success : StatusType.default}>
                        {isCompleted
                            ? (isReturn ? t('shippingOrders.unloadingCompleted') : t('shippingOrders.loadingCompleted'))
                            : (isReturn ? t('shippingOrders.unloadingNotCompleted') : t('shippingOrders.loadingNotCompleted'))
                        }
                    </Status>
                </div>
            </div>
            {showShippingOrderModal &&
                <ShippingOrderModal
                    shippingOrder={shippingOrder}
                    reservation={reservation}
                    onClose={() => setShowShippingOrderModal(false)}
                />
            }
        </>
    )
}

export default ShippingOrderSentToStockCoordinator
